import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FlatList, HStack, View } from 'native-base';

import { Text } from '@pimm/base';
import { LAYOUT } from '../consts';
import { useSalesLaborTracker } from '../context/sales-labor-tracker.context';
import { type LaborRow, LaborTableRow } from './labor-table-row';
import { TimeSlots } from './time-slots';
import { SalesDaypart } from '../types';

const LABOR_ROWS: LaborRow[] = [
  { key: 'scheduled', label: 'scheduled', getValue: s => s.laborScheduled, isGroupHeader: true },
  { key: 'scheduled_svc', label: 'service', getValue: s => s.laborSchedSvc, isIndented: true },
  { key: 'scheduled_ns', label: 'non_service', getValue: s => s.laborSchedNS, isIndented: true },
  { key: 'guide', label: 'guide', getValue: s => s.laborGuide, isGroupHeader: true },
  { key: 'guide_svc', label: 'service', getValue: s => s.laborGuideSvc, isIndented: true },
  { key: 'guide_ns', label: 'non_service', getValue: s => s.laborGuideNS, isIndented: true },
  { key: 'actual', label: 'actual', getValue: s => s.laborActual, isGroupHeader: true, hideWhenNotCompleted: true },
  { key: 'salary', label: 'salary', getValue: s => s.laborSalary, isIndented: true, hideWhenNotCompleted: true },
  { key: 'hourly', label: 'hourly', getValue: s => s.laborHourly, isIndented: true, hideWhenNotCompleted: true },
  {
    key: 'variance_actual',
    label: 'variance_actual',
    getValue: s => s.laborVar,
    isBold: true,
    isVariance: true,
    hideWhenNotCompleted: true,
  },
  {
    key: 'variance_cumulative',
    label: 'variance_cumulative',
    getValue: s => s.laborCumVar,
    isBold: true,
    isVariance: true,
    hideWhenNotCompleted: true,
  },
];

type LaborTableProps = {
  daypart?: Partial<SalesDaypart>;
  isDetailed?: boolean;
};

export const LaborTable = ({ daypart, isDetailed }: LaborTableProps) => {
  const { t } = useTranslation();
  const displayForecastedSales = useSalesLaborTracker(_ => _.displayForecastedSales);

  const rows = useMemo(() => {
    return LABOR_ROWS.filter(_ => isDetailed || !_.isIndented);
  }, [displayForecastedSales, isDetailed, daypart]);

  return (
    <View w="full" rounded={10} borderWidth={1} overflow="hidden">
      {/* Column headers */}
      <HStack bg="coolGray.50" borderBottomWidth={1}>
        <HStack alignItems="center" px={2.5} py={1} w={LAYOUT.stickyLabelW} minH={6} borderRightWidth={1}>
          <Text size="sm" fontWeight={700} color="black">
            {t('sales_tracker:labor')}
          </Text>
        </HStack>
        <TimeSlots tableData={daypart} />
      </HStack>

      {/* Data rows */}
      <FlatList
        bg="white"
        data={rows}
        scrollEnabled={false}
        keyExtractor={item => item.key}
        ItemSeparatorComponent={() => <Box h="1px" bg="gray.100" />}
        renderItem={({ item: row }) => <LaborTableRow row={row} tableData={daypart} />}
      />
    </View>
  );
};
